/**
 * Bid Workup / Estimating Service API
 * Rate sheets, equipment library, bids, NLP parsing, and proposal export
 */

import { createAPIClient } from '../client'
import { API_CONFIG } from '../config'

const client = createAPIClient(API_CONFIG.BID_WORKUP.baseURL, API_CONFIG.BID_WORKUP.timeout)

export const bidWorkupService = {
  // ==================== Rate Sheet ====================

  /**
   * Get all labor rates
   * @returns {Promise} Array of rate objects
   */
  getRates: async () => {
    const response = await client.get('/rates/')
    return response.data
  },

  // ── Capital proposal document ────────────────────────────────────────
  // The proposal as ordered sections rather than seven fixed fields. See
  // backend/app/api/routes/proposal_doc.py.
  getProposalDocument: async (bidId) => {
    const response = await client.get(`/proposal-doc/${bidId}`)
    return response.data
  },

  saveProposalDocument: async (bidId, sections) => {
    const response = await client.put(`/proposal-doc/${bidId}`, { sections })
    return response.data
  },

  seedProposalDocument: async (bidId) => {
    const response = await client.post(`/proposal-doc/${bidId}/seed`)
    return response.data
  },

  adoptProposalTemplate: async (bidId) => {
    const response = await client.post(`/proposal-doc/${bidId}/adopt`)
    return response.data
  },

  getProposalTemplates: async () => {
    const response = await client.get('/proposal-doc/templates')
    return response.data
  },

  saveProposalTemplates: async (sections) => {
    const response = await client.put('/proposal-doc/templates', { sections })
    return response.data
  },

  getClassifications: async () => {
    const response = await client.get('/rates/classifications')
    return response.data
  },

  /**
   * Get a single rate by ID
   * @param {number} id - Rate ID
   * @returns {Promise} Rate object
   */
  getRate: async (id) => {
    const response = await client.get(`/rates/${id}`)
    return response.data
  },

  /**
   * Create or update a labor rate
   * @param {object} rateData - Rate data { classification, base_rate, burden_pct, markup_pct, ot_multiplier, notes }
   * @returns {Promise} { ok: true }
   */
  saveRate: async (rateData) => {
    const response = await client.post('/rates/', rateData)
    return response.data
  },

  /**
   * Delete a labor rate
   * @param {number} id - Rate ID
   * @returns {Promise} { ok: true }
   */
  deleteRate: async (id) => {
    const response = await client.delete(`/rates/${id}`)
    return response.data
  },

  // ==================== Equipment Library ====================

  /**
   * Get equipment names only — no financial data, available to all users (for dropdowns).
   * @returns {Promise} Array of { id, name }
   */
  getEquipmentNames: async () => {
    const response = await client.get('/equipment/names')
    return response.data
  },

  /**
   * Get all equipment items
   * @returns {Promise} Array of equipment objects
   */
  getEquipment: async () => {
    const response = await client.get('/equipment/')
    return response.data
  },

  /**
   * Get a single equipment item by ID
   * @param {number} id - Equipment ID
   * @returns {Promise} Equipment object
   */
  getEquipmentItem: async (id) => {
    const response = await client.get(`/equipment/${id}`)
    return response.data
  },

  // NO saveEquipment / deleteEquipment HERE (PLAN-30 release A, fix round). The list of machine types is
  // changed only through HR's routes (`hrService.saveEquipment` / `deleteEquipment`, which Fleet > Machine
  // types calls); Bid Workup's write routes answer 410. Nothing called these two.

  // ==================== Bids ====================

  /**
   * Get all bids (summary list)
   * @returns {Promise} Array of bid summary objects
   */
  getBids: async () => {
    const response = await client.get('/bids/')
    return response.data
  },

  /**
   * Get a single bid with full details, including rates + equipment library
   * @param {number} id - Bid ID
   * @returns {Promise} { bid, rates, equipment_lib, rates_lookup }
   */
  getBid: async (id) => {
    const response = await client.get(`/bids/${id}`)
    return response.data
  },

  /**
   * Create a new blank bid
   * @returns {Promise} { ok: true, bid_id: number }
   */
  createBid: async () => {
    const response = await client.post('/bids/')
    return response.data
  },

  /**
   * Save / update a bid (full payload with line items)
   * @param {number} id - Bid ID
   * @param {object} bidData - Full bid save payload
   * @returns {Promise} { ok: true }
   */
  saveBid: async (id, bidData) => {
    const response = await client.put(`/bids/${id}`, bidData)
    return response.data
  },

  /**
   * Update a bid's status (lightweight patch)
   * @param {number} id - Bid ID
   * @param {string} status - New status
   * @returns {Promise} { ok: true }
   */
  updateBidStatus: async (id, status) => {
    const response = await client.patch(`/bids/${id}/status`, { status })
    return response.data
  },

  /**
   * "Use the current machine rules" (Capex completion C4, review round 1): a bid saved before C4 moves onto them only
   * when asked - unlocked, audited, re-priced.
   * @param {number} id - Bid ID
   * @returns {Promise} { changed, machine_rules, total_before, total_after, words }
   */
  useCurrentMachineRules: async (id) => {
    const response = await client.post(`/bids/${id}/machine-rules`)
    return response.data
  },

  /**
   * "Machine costs changed" on a SERVICE bid (Capex completion C6, PLAN-30 E3): each machine line whose Fleet figure by
   * the company's rule is not its cost. An offer; nothing is written. Costs - 403 for a login not shown them.
   * @returns {Promise} { changed: [{ line_id, name, per, was, now, rule }], old_rules, rule }
   */
  serviceMachineCostsChanged: async (id) => {
    const response = await client.get(`/bids/${id}/machine-costs/changed`)
    return response.data
  },

  /** "Take ticked": Fleet's figure on exactly the lines named, each as shown (409 if it moved, 423 locked). */
  takeServiceMachineCosts: async (id, items) => {
    const response = await client.post(`/bids/${id}/machine-costs/take`, { items })
    return response.data
  },

  /**
   * Delete a bid
   * @param {number} id - Bid ID
   * @returns {Promise} { ok: true }
   */
  deleteBid: async (id) => {
    const response = await client.delete(`/bids/${id}`)
    return response.data
  },

  /**
   * Duplicate a bid
   * @param {number} id - Bid ID to duplicate
   * @returns {Promise} { ok: true, new_bid_id: number }
   */
  duplicateBid: async (id) => {
    const response = await client.post(`/bids/${id}/duplicate`)
    return response.data
  },

  /**
   * Refresh bid rates from current rate sheet
   * @param {number} id - Bid ID
   * @returns {Promise} { ok: true }
   */
  refreshBidRates: async (id) => {
    const response = await client.post(`/bids/${id}/refresh-rates`)
    return response.data
  },

  /**
   * Get bid cost summary
   * @param {number} id - Bid ID
   * @returns {Promise} Summary object with totals
   */
  getBidSummary: async (id) => {
    const response = await client.get(`/bids/${id}/summary`)
    return response.data
  },

  /**
   * Get reference data (rates, equipment library, rates_lookup, categories)
   * in a single call. Used when creating a new bid to populate dropdowns.
   * @returns {Promise} { rates, equipment_lib, rates_lookup, categories }
   */
  getReferenceData: async () => {
    const response = await client.get('/bids/reference-data')
    return response.data
  },

  // ==================== NLP Parsing ====================

  /**
   * Parse scope text using NLP (LLM + regex fallback)
   * @param {number} bidId - Bid ID
   * @param {object} data - { scope_text: string }
   * @returns {Promise} Parsed labor, equipment, materials
   */
  parseScope: async (bidId, data) => {
    // bid_id is unused in the parse logic — pass 0 for unsaved bids
    const url = `/bids/${bidId || 0}/nlp-parse`
    const response = await client.post(url, data)
    return response.data
  },

  /**
   * Save a user-defined alias for NLP parsing
   * @param {string} original_text - Unmatched text
   * @param {string} mapped_classification - The valid classification to map to
   * @returns {Promise} { status: "ok" }
   */
  saveNlpAlias: async (original_text, mapped_classification) => {
    const response = await client.post('/bids/nlp-aliases', { original_text, mapped_classification })
    return response.data
  },

  // ==================== Export ====================

  exportBid: async (bidId, format) => {
    const response = await client.get(`/bids/${bidId}/export/${format}`, {
      responseType: 'blob',
    })
    return response.data
  },

  /**
   * Send bid proposal to client via email
   * @param {number} bidId - Bid ID
   * @returns {Promise} { status: "success", message: "..." }
   */
  emailClient: async (bidId, payload) => {
    const response = await client.post(`/bids/${bidId}/email-client`, payload)
    return response.data
  },

  getBidDocuments: async (bidId) => {
    const response = await client.get(`/bids/${bidId}/documents`)
    return response.data
  },

  /**
   * Upload a reference document
   */
  // `documentType` defaults to what this always sent, so every existing caller is unchanged. The
  // route has always accepted the field; the frontend simply hardcoded it and never offered it,
  // which is why a bid could carry the drawings it was priced from with no way to say so.
  uploadBidDocument: async (bidId, file, includeInProposal, documentType = 'Reference') => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('document_type', documentType)
    formData.append('include_in_proposal', includeInProposal ? 'true' : 'false')
    
    const response = await client.post(`/bids/${bidId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  /**
   * Generate a new bid document and save it
   * @param {number} bidId - Bid ID
   * @param {string} documentType - e.g., "Proposal", "Cost Breakdown"
   * @param {string} fmt - "pdf" or "docx"
   */
  generateDocument: async (bidId, documentType, fmt) => {
    const response = await client.post(`/bids/${bidId}/documents/generate`, null, {
      params: { document_type: documentType, fmt }
    })
    return response.data
  },

  /**
   * Download a stored document using fetch blob to enforce octet-stream locally
   */
  downloadDocument: async (bidId, docId) => {
    const response = await client.get(`/bids/${bidId}/documents/${docId}/download`, { responseType: 'blob' })
    return response.data
  },

  /**
   * THE EXECUTED COPY of a signed proposal — both marks and the signature certificate — through the
   * signed-in client (review finding 18). A plain link to the route carries no login token, so the
   * browser's new tab showed "Not authenticated" instead of the PDF.
   */
  getSignedCopy: async (bidId, docId) => {
    const response = await client.get(`/bids/${bidId}/documents/${docId}/signed-copy`, { responseType: 'blob' })
    return response.data
  },

  // ==================== Proposal signatures ====================
  //
  // TWO GATES on the company signature, and they answer different questions. `bid:sign_proposal`
  // says who MAY be asked — a role question, manager and above by default. The assignment says who
  // HAS been asked. The emailed link is not the credential: a forwarded message reaches somebody
  // who cannot sign, which is what stops it binding the company to a price.

  /** Where this proposal stands, plus every hop of who was asked and why it moved. */
  getSigningStatus: async (bidId, docId) => {
    const response = await client.get(`/bids/${bidId}/documents/${docId}/signing`)
    return response.data
  },

  /** Ask a colleague to sign for the company. Freezes the document and emails them. */
  requestProposalSignature: async (bidId, docId, { assigneeUserId, assigneeName, assigneeEmail }) => {
    const response = await client.post(`/bids/${bidId}/documents/${docId}/request-signature`, {
      document_id: docId,
      assignee_user_id: assigneeUserId,
      assignee_name: assigneeName,
      assignee_email: assigneeEmail,
    })
    return response.data
  },

  /** Hand it to somebody else. Immediate; the sender is told and may veto. Reason required. */
  reassignProposalSignature: async (bidId, docId, { assigneeUserId, assigneeName, assigneeEmail, reason }) => {
    const response = await client.post(`/bids/${bidId}/documents/${docId}/reassign`, {
      document_id: docId,
      assignee_user_id: assigneeUserId,
      assignee_name: assigneeName,
      assignee_email: assigneeEmail,
      reason,
    })
    return response.data
  },

  /** The sender's veto — ends the request without signing it. */
  withdrawProposalSignature: async (bidId, docId) => {
    const response = await client.post(`/bids/${bidId}/documents/${docId}/withdraw`, {
      document_id: docId,
    })
    return response.data
  },

  /**
   * The exact sentence the signer is agreeing to.
   *
   * Fetched rather than written into this page, so the wording that gets STORED is always the
   * server's own. A consent string supplied by the browser is worthless as evidence, because the
   * party who benefits from the wording would be the party supplying it.
   */
  getProposalConsent: async (bidId, docId, locale = 'en') => {
    const response = await client.get(`/bids/${bidId}/documents/${docId}/consent`, {
      params: { locale },
    })
    return response.data
  },

  /** Sign for the company. Only the assigned signer succeeds — enforced server-side. */
  signProposalForCompany: async (bidId, docId, { signature, signedName, consent }) => {
    const response = await client.post(`/bids/${bidId}/documents/${docId}/sign`, {
      document_id: docId,
      signature,
      signed_name: signedName,
      // Echoed back verbatim under the server's own field names, so the wording and its identifier
      // cannot drift apart on the way through.
      consent_key: consent.consent_key,
      consent_version: consent.consent_version,
      consent_locale: consent.consent_locale,
    })
    return response.data
  },

  /**
   * Mint the client's signing link and email it.
   *
   * NOT automatic on the company signature, at the owner's instruction: a superior signing is the
   * approval, but the moment a priced document leaves the building belongs to the person who owns
   * the relationship.
   */
  // NO `cc`. This mints and mails the link that SIGNS the proposal, and the signing page checks
  // no identity, so a copied address is a second party who can commit the client to a price. The
  // server rejects the field outright as of 2026-08-23; it is gone here too so nobody adds a Cc box
  // to the panel and wonders why it 422s.
  sendProposalToClient: async (bidId, docId, { toEmail } = {}) => {
    const response = await client.post(`/bids/${bidId}/documents/${docId}/send-to-client`, {
      to_email: toEmail || null,
    })
    return response.data
  },

  /**
   * Delete a stored document
   */
  deleteDocument: async (bidId, docId) => {
    const response = await client.delete(`/bids/${bidId}/documents/${docId}`)
    return response.data
  },

  // ==================== Revisions ====================

  /**
   * Get revision history for a bid
   * @param {number} bidId - Bid ID
   * @returns {Promise} Array of revision summaries
   */
  getRevisions: async (bidId) => {
    const response = await client.get(`/bids/${bidId}/revisions`)
    return response.data
  },

  /**
   * Get a specific revision detail
   * @param {number} bidId - Bid ID
   * @param {number} revisionId - Revision ID
   * @returns {Promise} Revision detail with snapshot
   */
  getRevision: async (bidId, revisionId) => {
    const response = await client.get(`/bids/${bidId}/revisions/${revisionId}`)
    return response.data
  },

  // ==================== Cross-Module Integration ====================

  /**
   * Update only the job_number on a bid (used by CRM convert-to-project)
   * @param {number} bidId - Bid ID
   * @param {string} jobNumber - The project number to save as job number
   * @returns {Promise} { ok: true }
   */
  updateBidJobNumber: async (bidId, jobNumber) => {
    const response = await client.patch(`/bids/${bidId}/job-number`, { job_number: jobNumber })
    return response.data
  },

  /**
   * Health check — is the Bid Workup service reachable?
   * Uses the root health endpoint (no auth required).
   * @returns {Promise<boolean>} true if healthy
   */
  checkHealth: async () => {
    try {
      const baseURL = client.defaults?.baseURL || ''
      const healthURL = baseURL.replace(/\/api\/v1\/?$/, '/health')
      const resp = await fetch(healthURL, { method: 'GET', signal: AbortSignal.timeout(3000) })
      if (!resp.ok) return false
      const data = await resp.json()
      return data.status === 'healthy'
    } catch {
      return false
    }
  },

  // ==================== Company Settings (Branding) ====================

  getSettings: async () => {
    const response = await client.get('/settings')
    return response.data
  },

  updateSettings: async (payload) => {
    const response = await client.put('/settings', payload)
    return response.data
  },

  updateBidDefaults: async (payload) => {
    const response = await client.put('/settings/bid-defaults', payload)
    return response.data
  },

  updateSupervisionDefaults: async (supervision) => {
    const response = await client.put('/settings/supervision-defaults', { supervision })
    return response.data
  },

  uploadLogo: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await client.post('/settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  getLogoUrl: () => {
    const baseURL = client.defaults?.baseURL || ''
    return `${baseURL}/settings/logo/current`
  },

  getLogoFileUrl: (filename) => {
    const baseURL = client.defaults?.baseURL || ''
    return `${baseURL}/settings/logo/file/${encodeURIComponent(filename)}`
  },

  listLogos: async () => {
    const response = await client.get('/settings/logo/library')
    return response.data
  },

  selectLogo: async (filename) => {
    const response = await client.put('/settings/logo/select', { filename })
    return response.data
  },

  // ==================== Categories ====================

  /**
   * Get all bid categories
   * @param {boolean} includeInactive - Include inactive categories
   * @returns {Promise} Array of category objects
   */
  getCategories: async (includeInactive = false) => {
    const response = await client.get('/categories/', { params: { include_inactive: includeInactive } })
    return response.data
  },

  /**
   * Create a new bid category
   * @param {object} data - { name, description, color }
   * @returns {Promise} { ok: true, category_id: number }
   */
  createCategory: async (data) => {
    const response = await client.post('/categories/', data)
    return response.data
  },

  /**
   * Update a bid category
   * @param {number} id - Category ID
   * @param {object} data - { name, description, color, is_active }
   * @returns {Promise} { ok: true }
   */
  updateCategory: async (id, data) => {
    const response = await client.put(`/categories/${id}`, data)
    return response.data
  },

  /**
   * Delete (deactivate) a bid category
   * @param {number} id - Category ID
   * @returns {Promise} { ok: true }
   */
  deleteCategory: async (id) => {
    const response = await client.delete(`/categories/${id}`)
    return response.data
  },

  // ==================== Bid Requests ====================

  /**
   * Generate an AI-assisted scope of work paragraph
   * @param {string} description - Raw project description to interpret
   * @returns {Promise} { scope_of_work: string, ai_available: bool }
   */
  generateScope: async (description) => {
    const response = await client.post('/bid-requests/generate-scope', { description })
    return response.data
  },

  /**
   * Submit a new bid request
   * @param {object} data - Bid request fields
   * @returns {Promise} { id, status, ... }
   */
  submitBidRequest: async (data) => {
    const response = await client.post('/bid-requests/', data)
    return response.data
  },

  /**
   * Get the current user's own bid requests
   * @returns {Promise} Array of bid request objects
   */
  getMyBidRequests: async () => {
    const response = await client.get('/bid-requests/my')
    return response.data
  },

  /**
   * Get all bid requests (estimators+ only)
   * @param {string} [status] - Optional status filter
   * @returns {Promise} Array of bid request objects
   */
  getBidRequests: async (status) => {
    const params = status ? { status } : {}
    const response = await client.get('/bid-requests/', { params })
    return response.data
  },

  /**
   * Get a single bid request by ID
   * @param {number} id - Bid request ID
   * @returns {Promise} Bid request object
   */
  getBidRequest: async (id) => {
    const response = await client.get(`/bid-requests/${id}`)
    return response.data
  },

  /**
   * Assign a bid request to an estimator and create a pre-populated bid
   * @param {number} id - Bid request ID
   * @param {object} data - { estimator_notes }
   * @returns {Promise} { ok, new_bid_id }
   */
  assignBidRequest: async (id, data) => {
    const response = await client.post(`/bid-requests/${id}/assign`, data)
    return response.data
  },

  /**
   * Reject a bid request
   * @param {number} id - Bid request ID
   * @param {object} data - { notes }
   * @returns {Promise} { ok }
   */
  rejectBidRequest: async (id, data) => {
    const response = await client.post(`/bid-requests/${id}/reject`, data)
    return response.data
  },

  /**
   * Get KPI stats for bid requests (scoped to current user or global for estimators+)
   */
  getBidRequestStats: async () => {
    const response = await client.get('/bid-requests/stats')
    return response.data
  },

  // ── Labor Templates ────────────────────────────────────

  getLaborTemplates: async () => {
    const response = await client.get('/labor-templates/')
    return response.data
  },

  createLaborTemplate: async (data) => {
    const response = await client.post('/labor-templates/', data)
    return response.data
  },

  updateLaborTemplate: async (id, data) => {
    const response = await client.put(`/labor-templates/${id}`, data)
    return response.data
  },

  deleteLaborTemplate: async (id) => {
    const response = await client.delete(`/labor-templates/${id}`)
    return response.data
  },

  // ── Comments ───────────────────────────────────────

  getComments: async (reqId) => {
    const response = await client.get(`/bid-requests/${reqId}/comments`)
    return response.data
  },

  addComment: async (reqId, body) => {
    const response = await client.post(`/bid-requests/${reqId}/comments`, { body })
    return response.data
  },

  // ── Notifications ──────────────────────────────────

  getNotificationsUrl: () => {
    const baseURL = client.defaults?.baseURL || ''
    return `${baseURL}/notifications/stream`
  },

  // B2: mint a short-lived (~45s) SSE ticket over the normal authed channel (this POST carries the
  // Authorization: Bearer header via the client interceptor — EventSource cannot). The caller puts the
  // ticket in the EventSource URL instead of the full access token, so a logged URL is useless in seconds.
  getStreamTicket: async () => {
    const response = await client.post('/notifications/stream-ticket')
    return response.data // { ticket, expires_in }
  },

  getNotifications: async (unreadOnly = false) => {
    const response = await client.get('/notifications/', { params: { unread_only: unreadOnly } })
    return response.data
  },

  getUnreadCount: async () => {
    const response = await client.get('/notifications/count')
    return response.data
  },

  markNotificationRead: async (id) => {
    const response = await client.post(`/notifications/${id}/read`)
    return response.data
  },

  markAllNotificationsRead: async () => {
    const response = await client.post('/notifications/read-all')
    return response.data
  },

  // ==================== NLP Config (Aliases & Templates) ====================

  getLaborAliases: async () => {
    const response = await client.get('/nlp-config/labor-aliases')
    return response.data
  },

  createLaborAlias: async (data) => {
    const response = await client.post('/nlp-config/labor-aliases', data)
    return response.data
  },

  deleteLaborAlias: async (id) => {
    const response = await client.delete(`/nlp-config/labor-aliases/${id}`)
    return response.data
  },

  getEquipmentAliases: async () => {
    const response = await client.get('/nlp-config/equipment-aliases')
    return response.data
  },

  createEquipmentAlias: async (data) => {
    const response = await client.post('/nlp-config/equipment-aliases', data)
    return response.data
  },

  deleteEquipmentAlias: async (id) => {
    const response = await client.delete(`/nlp-config/equipment-aliases/${id}`)
    return response.data
  },

  getScopeTemplates: async () => {
    const response = await client.get('/nlp-config/scope-templates')
    return response.data
  },

  createScopeTemplate: async (data) => {
    const response = await client.post('/nlp-config/scope-templates', data)
    return response.data
  },

  updateScopeTemplate: async (id, data) => {
    const response = await client.put(`/nlp-config/scope-templates/${id}`, data)
    return response.data
  },

  deleteScopeTemplate: async (id) => {
    const response = await client.delete(`/nlp-config/scope-templates/${id}`)
    return response.data
  },

  learnCorrection: async (data) => {
    const response = await client.post('/nlp-config/learn-correction', data)
    return response.data
  },

  getSchedulePresets: async () => {
    const response = await client.get('/settings/schedules')
    return response.data
  },

  createSchedulePreset: async (payload) => {
    const response = await client.post('/settings/schedules', payload)
    return response.data
  },

  updateSchedulePreset: async (presetId, payload) => {
    const response = await client.put(`/settings/schedules/${presetId}`, payload)
    return response.data
  },

  deleteSchedulePreset: async (presetId) => {
    const response = await client.delete(`/settings/schedules/${presetId}`)
    return response.data
  },

  // ==================== Capital Project mode ====================
  // A capital bid does NOT use useBidSummary. Two implementations of a fourteen-step waterfall
  // will diverge, and the failure mode is a proposal PDF that disagrees with the screen somebody
  // approved. Every number below comes from the server; the browser only draws it.

  /** The stored structure: WBS, takeoff, crews and work items. No money. */
  getCapex: async (bidId) => {
    const response = await client.get(`/capex/${bidId}`)
    return response.data
  },

  /** The priced view: the fourteen steps, the by-system breakdown and the enriched work items. */
  getCapexSummary: async (bidId) => {
    const response = await client.get(`/capex/${bidId}/summary`)
    return response.data
  },

  /**
   * Save the whole structure in one transaction and get the new price back in the same response.
   * Omitting a list leaves that table alone; sending an empty one deletes its rows.
   * New rows may carry `client_ref`, and children may point at them with `wbs_ref` / `crew_ref` /
   * `takeoff_ref` — the server mints the ids in foreign-key order and returns the mapping.
   */
  saveCapex: async (bidId, payload) => {
    const response = await client.put(`/capex/${bidId}`, payload)
    return response.data
  },

  setProjectType: async (bidId, payload) => {
    const response = await client.put(`/capex/${bidId}/project-type`, payload)
    return response.data
  },

  /**
   * Award a won CAPITAL bid: create the project, freeze the baseline, close the opportunity and
   * mark the bid — all in ONE server-side transaction, or none of it.
   *
   * Replaces four independent browser calls for capital bids. Those could each fail alone, and two
   * of them caught the failure and carried on, so a project linked to an opportunity with no bid and
   * a bid marked Awarded against no project were both reachable with nothing recording which.
   *
   * It lives on the BID WORKUP service rather than the capital one because a transaction belongs to
   * a connection and a connection belongs to a process — and only this service can both price the
   * bid and write every table. See Bid_Workup/backend/app/core/capital_award.py.
   *
   * Service bids are refused with a 400 and keep the existing convert flow.
   *
   * @param {number} bidId
   * @param {object} data - { project_number?, project_name?, client_name?, start_date?,
   *                          planned_end_date?, opportunity_id?, project_id?, reason?, note? }
   * @returns {Promise<object>} { project_id, capital_project_id, baseline_id, version,
   *                              total_price, estimated_hours, priced_hours, hours_coverage,
   *                              contract_type, contract_type_from_bid, ... }
   */
  awardCapitalBid: async (bidId, data = {}) => {
    const response = await client.post(`/capex/${bidId}/award`, data)
    return response.data
  },

  /**
   * The company's active departments, for the capital award's department pick (C1 item 3): a foreman
   * sees only his own department's jobs, so the award asks which. `{ departments: [{ id, name }] }`;
   * empty when the company has none, and then nothing is asked.
   */
  getAwardDepartments: async () => {
    const response = await client.get('/capex/award-departments')
    return response.data
  },

  /**
   * Where a capital estimate stands — the Capex estimate shell's one read
   * (GET /capital-estimates/{id}): its stage, its header, its standing and, once awarded, the
   * `award` block (project number, frozen version, the Capex job). The award panel reads the
   * project number off it so "Award again" names the project it is about to re-freeze.
   */
  getCapitalEstimate: async (bidId) => {
    const response = await client.get(`/capital-estimates/${bidId}`)
    return response.data
  },

  /**
   * Award a SERVICE bid: create the project and close its opportunity, in one transaction.
   *
   * A capital bid goes to `awardCapitalBid` instead, which also freezes a baseline from its work
   * breakdown; the server refuses a capital bid on this route rather than quietly dropping it.
   *
   * @param {number} bidId
   * @param {object} data - { project_number?, project_name?, client_name?, start_date?,
   *                          planned_end_date?, opportunity_id? } — the bid answers for anything
   *                          omitted, which is the point: it has been typed once already.
   * @returns {Promise<object>} { project_id, project_number, project_name, opportunity_id,
   *                              contract_type, contract_type_from_bid }
   */
  awardServiceBid: async (bidId, data = {}) => {
    const response = await client.post(`/bids/${bidId}/award`, data)
    return response.data
  },

  /**
   * What a Convert of this SERVICE bid would build as the project's budget, before the button is
   * pressed: the basis the company chose (`basis`: 'estimated_cost' | 'selling_price', or null when
   * nobody has - then `not_set` says so), the five header buckets and their total (`budget`, with
   * `withheld_fields` - cost figures are withheld from a caller the bid read withholds cost from),
   * and how many lines of each kind. The same plan the build below writes.
   *
   * @param {number} bidId
   * @returns {Promise<object>} { basis, basis_label, not_set?, budget, lines, uncosted_lines,
   *                              factor_costed_lines }
   */
  getConvertedProjectBudget: async (bidId) => {
    const response = await client.get(`/bids/${bidId}/project-budget`)
    return response.data
  },

  /**
   * Build the budget of the project an opportunity's Convert has just made from this SERVICE bid -
   * its header buckets and its lines, on the company's basis - on the server, from the bid as stored
   * (Bid_Workup/backend/app/core/convert_budget.py).
   *
   * The Convert tab used to build the lines in the browser from `getBid`, which withholds every
   * labour line's wage from a caller without the money, so a coordinator's Convert wrote an all-$0
   * labour budget. Send only the two ids: the server works out every figure, and answers counts.
   *
   * @param {number} bidId
   * @param {string} projectId - the project the Convert just created (its `source_bid_id` is bidId)
   * @returns {Promise<object>} { ok, project_id, basis, lines, labor, supervision, equipment,
   *                              uncosted_lines, factor_costed_lines }
   */
  buildConvertedProjectBudget: async (bidId, projectId) => {
    const response = await client.post(
      `/bids/${bidId}/project-budget/${encodeURIComponent(projectId)}`)
    return response.data
  },

  /** The stored commercial terms and adjustment lines. No money — /summary carries that. */
  getCapexTerms: async (bidId) => {
    const response = await client.get(`/capex/${bidId}/terms`)
    return response.data
  },

  /**
   * Save the commercial terms and get the repriced waterfall back in the same response.
   *
   * SEND ONLY THE KEYS YOU MEAN TO CHANGE. The server distinguishes an ABSENT key ("leave this
   * column alone") from an explicit `null` ("clear it to NULL"), because a NULL here means NOT
   * CONFIGURED and never zero — an unset bond is not a 0% bond. Spreading a whole form object in
   * would send every key on every save, so a field the estimator has not touched would be
   * rewritten with whatever the form happened to hold.
   *
   * `adjustments` follows the same convention as saveCapex's lists: omitting it leaves the lines
   * alone, sending an EMPTY array deletes them all.
   */
  saveCapexTerms: async (bidId, payload) => {
    const response = await client.put(`/capex/${bidId}/terms`, payload)
    return response.data
  },

  /** What the price would ACTUALLY drop by if this scope left — not its allocated share. */
  getScopeDelta: async (bidId, wbsId) => {
    const response = await client.get(`/capex/${bidId}/scope-delta/${wbsId}`)
    return response.data
  },

  crewFromTemplate: async (bidId, templateId) => {
    const response = await client.post(`/capex/${bidId}/crew-from-template/${templateId}`)
    return response.data
  },

  deleteCapexCrew: async (bidId, crewId) => {
    const response = await client.delete(`/capex/${bidId}/crews/${crewId}`)
    return response.data
  },

  getUnitRates: async (includeInactive = false) => {
    const response = await client.get('/unit-rates/', { params: { include_inactive: includeInactive } })
    return response.data
  },

  saveUnitRate: async (payload) => {
    const response = await client.post('/unit-rates/', payload)
    return response.data
  },

  deleteUnitRate: async (rateId) => {
    const response = await client.delete(`/unit-rates/${rateId}`)
    return response.data
  }
}
